import React from 'react'
import styled from 'styled-components/macro'
import {
  formatSuccessHubCollapsedSetsSummary,
  formatSuccessHubCollectionNext,
  formatSuccessHubCollectionNextHow,
  formatSuccessHubCollectionSummary,
  isSuccessHubCollectionComplete,
  isSuccessHubCollectionVisible,
} from 'src/utils/growthEngineSuccessHub'
import {
  SUCCESS_HUB_PROGRESS_TOKENS,
  successHubProgressRowBorder,
} from 'src/utils/successHubProgressTokens'

const T = SUCCESS_HUB_PROGRESS_TOKENS

/**
 * Progress collections under Locked in (29.43): open set stays expanded with
 * how-to; finished sets collapse to one quiet “N sets locked in” line.
 */
const SuccessHubCollections = ({
  featuredCollection,
  collapsedCollections = [],
  'data-testid': dataTestId = 'progress-collections',
}) => {
  const collapsed = (collapsedCollections || []).filter(
    isSuccessHubCollectionVisible,
  )
  const featured =
    featuredCollection?.id && isSuccessHubCollectionVisible(featuredCollection)
      ? featuredCollection
      : null

  const featuredOpen =
    featured && !isSuccessHubCollectionComplete(featured) ? featured : null
  const openCollapsed = collapsed.filter(
    collection => !isSuccessHubCollectionComplete(collection),
  )
  const completeCount =
    (featured && isSuccessHubCollectionComplete(featured) ? 1 : 0) +
    collapsed.filter(isSuccessHubCollectionComplete).length
  const collapsedSummary = formatSuccessHubCollapsedSetsSummary(completeCount)

  if (!featuredOpen && openCollapsed.length === 0 && !collapsedSummary) {
    return null
  }

  return (
    <Wrap data-testid={dataTestId}>
      {featuredOpen ? (
        <CollectionRow collection={featuredOpen} featured />
      ) : null}

      {openCollapsed.map(collection => (
        <CollectionRow key={collection.id} collection={collection} />
      ))}

      {collapsedSummary ? (
        <CollapsedSummary data-testid='progress-collections-collapsed'>
          {collapsedSummary}
        </CollapsedSummary>
      ) : null}
    </Wrap>
  )
}

const CollectionRow = ({ collection, featured = false }) => {
  const summary = formatSuccessHubCollectionSummary(collection)
  const next = formatSuccessHubCollectionNext(collection)
  const how = formatSuccessHubCollectionNextHow(collection)
  const earned = Number(collection.earnedCount)
  const total = Number(collection.total)
  const showBar = Number.isFinite(earned) && Number.isFinite(total) && total > 0
  const ratio = showBar ? Math.min(1, Math.max(0, earned / total)) : 0
  const complete = isSuccessHubCollectionComplete(collection)
  const testId = featured
    ? 'progress-collection-featured'
    : `progress-collection-${collection.id}`

  return (
    <Open data-testid={testId} $featured={featured}>
      <Name
        data-testid={
          featured
            ? 'progress-collection-featured-summary'
            : `${testId}-summary`
        }
      >
        {summary}
      </Name>
      {showBar ? (
        <BarTrack
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={earned}
          aria-label={`${
            collection.displayName || 'Collection'
          } ${earned} of ${total}`}
          data-testid={
            featured ? 'progress-collection-featured-bar' : `${testId}-bar`
          }
        >
          <BarFill style={{ width: `${ratio * 100}%` }} $complete={complete} />
        </BarTrack>
      ) : null}
      {next ? (
        <Next
          data-testid={
            featured ? 'progress-collection-featured-next' : `${testId}-next`
          }
        >
          {next}
        </Next>
      ) : null}
      {how ? (
        <How
          data-testid={
            featured ? 'progress-collection-featured-how' : `${testId}-how`
          }
        >
          {how}
        </How>
      ) : null}
    </Open>
  )
}

export default SuccessHubCollections

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space.sm};
  width: 100%;
`

const Open = styled.div`
  background: ${({ theme }) => theme?.colours?.paleGrey || '#f7f7f7'};
  border: ${successHubProgressRowBorder()};
  border-radius: ${T.radius.row};
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${T.pad.row};
`

const Name = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.rowTitle};
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Next = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.rowMeta};
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
  opacity: 0.78;
`

const How = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: ${T.type.lead};
  font-weight: 500;
  line-height: 1.45;
  margin: 0;
`

/** Quiet stack of finished sets — meta line only, no pale card / bar yell. */
const CollapsedSummary = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#919191'};
  font-size: ${T.type.meta};
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
`

const BarTrack = styled.div`
  background: ${({ theme }) => theme?.colours?.mayoWhite || '#fff'};
  border-radius: 999px;
  height: ${T.meter.collection};
  overflow: hidden;
  width: 100%;
`

const BarFill = styled.div`
  background: ${({ theme, $complete }) =>
    $complete
      ? theme?.colours?.oliveGrey || '#8c8c8c'
      : theme?.colours?.bbqBlack || '#262626'};
  border-radius: 999px;
  height: 100%;
  max-width: 100%;
  min-width: 0;
  opacity: ${({ $complete }) => ($complete ? 0.45 : 0.8)};
  transition: width 0.25s ease;
`
